import { Box, Grid, Stack, Typography } from "@mui/material";
import StarIcon from "@mui/icons-material/Star";
import LocalShippingOutlinedIcon from "@mui/icons-material/LocalShippingOutlined";
import MapOutlinedIcon from "@mui/icons-material/MapOutlined";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import BoltIcon from "@mui/icons-material/Bolt";
import VerifiedIcon from "@mui/icons-material/Verified";
import PaymentsIcon from "@mui/icons-material/Payments";
import TaskAltIcon from "@mui/icons-material/TaskAlt";
import SupportAgentIcon from "@mui/icons-material/SupportAgent";
import StraightenIcon from "@mui/icons-material/Straighten";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import styled, { keyframes } from "styled-components";
import deliveryHeroImage from "../../Assets/images/tazty_rider_madurai.png";
import {
  isTaztyBuild,
  PARTNER_PRIMARY_CONTAINER_HEX,
  PARTNER_PRIMARY_HEX,
} from "../../constants/partnerBrand";

const PRIMARY = PARTNER_PRIMARY_HEX;
const PRIMARY_CONTAINER = PARTNER_PRIMARY_CONTAINER_HEX;
const ON_SURFACE = "#151c27";
const ON_SURFACE_VARIANT = "#3d4a3d";
const SURFACE_LOW = "#f0f3ff";
const OUTLINE_VARIANT = "rgba(188, 203, 185, 0.35)";

/** Shared carousel typography — one scale across slides (display, body, pills, stats). */
const FONT_DISPLAY = '"Plus Jakarta Sans", "Inter", sans-serif';
const carouselType = {
  headline: {
    fontFamily: FONT_DISPLAY,
    fontSize: { xs: "2.125rem", xl: "3.25rem" },
    fontWeight: 800,
    letterSpacing: "-0.03em",
    color: ON_SURFACE,
    lineHeight: 1.1,
  },
  /** Slide 2 uses a slightly taller line for a two-line lockup. */
  headlineLoose: {
    fontFamily: FONT_DISPLAY,
    fontSize: { xs: "2.125rem", xl: "3.25rem" },
    fontWeight: 800,
    letterSpacing: "-0.03em",
    color: ON_SURFACE,
    lineHeight: 1.15,
  },
  body: {
    fontSize: "1.05rem",
    color: ON_SURFACE_VARIANT,
    lineHeight: 1.65,
  },
  eyebrow: {
    fontSize: 11,
    fontWeight: 800,
    letterSpacing: "0.14em",
    textTransform: "uppercase" as const,
    color: PRIMARY,
  },
  statValue: {
    fontSize: "1.5rem",
    fontWeight: 900,
    color: PRIMARY,
  },
  statLabel: {
    fontSize: 10,
    fontWeight: 800,
    letterSpacing: "0.14em",
    color: ON_SURFACE_VARIANT,
    textTransform: "uppercase" as const,
  },
  featureCardTitle: {
    fontSize: "1.125rem",
    fontWeight: 800,
    color: PRIMARY,
    lineHeight: 1.2,
  },
  featureListItem: {
    fontSize: "0.875rem",
    fontWeight: 700,
    color: ON_SURFACE,
  },
  testimonialQuote: {
    fontSize: "0.875rem",
    fontStyle: "italic" as const,
    color: ON_SURFACE_VARIANT,
    lineHeight: 1.6,
  },
  testimonialByline: {
    fontSize: 12,
    fontWeight: 700,
    color: ON_SURFACE,
  },
  imageCaption: {
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: "0.12em",
    textTransform: "uppercase" as const,
    color: "#fff",
  },
};

/** Hero image for carousel slide 2 (Tazty rider / Madurai delivery). */
const DELIVERY_HERO_IMAGE = deliveryHeroImage;

const CAROUSEL_PERIOD_S = 30;

/** Five slides at 20% width each (slide 1 “Grow your business” is commented out for now). */
const slide = keyframes`
  0%, 15% { transform: translateX(0%); }
  19%, 34% { transform: translateX(-20%); }
  38%, 53% { transform: translateX(-40%); }
  57%, 72% { transform: translateX(-60%); }
  76%, 91% { transform: translateX(-80%); }
  100% { transform: translateX(0%); }
`;

const dotPulse = keyframes`
  0%, 17%, 100% { opacity: 1; transform: scale(1.15); background-color: ${PRIMARY}; }
  20%, 97% { opacity: 0.35; transform: scale(1); background-color: ${PRIMARY}; }
`;

const CarouselRoot = styled(Box)`
  position: relative;
  width: 100%;
  height: 100%;
  min-height: 0;
  flex: 1;
  overflow: hidden;
  background-color: ${SURFACE_LOW};
`;

const CarouselTrack = styled.div`
  display: flex;
  height: 100%;
  width: 500%;
  transition: transform 1.2s cubic-bezier(0.83, 0, 0.17, 1);
  animation: ${slide} ${CAROUSEL_PERIOD_S}s infinite cubic-bezier(0.83, 0, 0.17, 1);

  @media (prefers-reduced-motion: reduce) {
    animation: none;
    transform: translateX(0);
    transition: none;
  }
`;

const Slide = styled(Box)`
  flex: 0 0 20%;
  height: 100%;
  min-height: 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 48px;
  position: relative;
  box-sizing: border-box;
  overflow: hidden;
`;

const Dot = styled.span<{ $delay: string }>`
  width: 12px;
  height: 12px;
  border-radius: 9999px;
  background-color: ${PRIMARY};
  animation: ${dotPulse} ${CAROUSEL_PERIOD_S}s infinite cubic-bezier(0.83, 0, 0.17, 1);
  animation-delay: ${({ $delay }) => $delay};

  @media (prefers-reduced-motion: reduce) {
    animation: none;
    opacity: 0.85;
    transform: none;
  }
`;

const testimonialImage =
  "https://images.unsplash.com/photo-1577219491135-ce391730fb2c?w=128&h=128&fit=crop";

/**
 * Tazty-only: partner marketing carousel for the Direct-channel sign-in shell.
 * Do not reuse for Valar/ONDC or other org builds — copy, imagery, and motion are Tazty-specific.
 */
const LoginMarketingCarousel: React.FC = () => {
  if (!isTaztyBuild()) {
    return null;
  }

  return (
    <CarouselRoot>
      <CarouselTrack>
        {/*
        Slide 1 — “Grow your business” bento (disabled for now). Re-enable with 6-slide track/keyframes/dots
        and imports: HandshakeOutlined, QueryStats, RestaurantMenu, Speed.
        <Slide sx={{ bgcolor: SURFACE_LOW }}>...</Slide>
        */}

        <Slide>
          <Box
            sx={{
              position: "absolute",
              top: 0,
              right: 0,
              width: 384,
              height: 384,
              borderRadius: "50%",
              bgcolor: `${PRIMARY_CONTAINER}1a`,
              filter: "blur(48px)",
              transform: "translate(40%, -40%)",
              pointerEvents: "none",
            }}
          />
          <Box sx={{ position: "relative", zIndex: 1, maxWidth: 520, mx: "auto" }}>
            <Typography
              component="span"
              sx={{
                display: "inline-block",
                px: 2,
                py: 0.75,
                mb: 3,
                ...carouselType.eyebrow,
                bgcolor: `${PRIMARY_CONTAINER}33`,
                borderRadius: 999,
              }}
            >
              Scale &amp; Impact
            </Typography>
            <Typography
              sx={{
                ...carouselType.headline,
                mb: 3,
              }}
            >
              Scale your <br />
              <Box component="span" sx={{ color: PRIMARY, fontStyle: "italic" }}>
                Restaurant
              </Box>{" "}
              vision.
            </Typography>
            <Typography sx={{ ...carouselType.body, mb: 4 }}>
              Join hundreds of restaurants using Tazty to manage operations, optimize menus,
              and reach a new generation of diners.
            </Typography>
            <Grid container spacing={2} sx={{ mb: 4 }}>
              {[
                { k: "25x", l: "Avg. Growth" },
                { k: "30%", l: "Lower Fees" },
                { k: "24/7", l: "VIP Support" },
              ].map((cell) => (
                <Grid item xs={4} key={cell.l}>
                  <Box
                    sx={{
                      p: 2,
                      bgcolor: "#fff",
                      borderRadius: 3,
                      border: `1px solid ${OUTLINE_VARIANT}`,
                      textAlign: "center",
                      boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
                    }}
                  >
                    <Typography sx={carouselType.statValue}>{cell.k}</Typography>
                    <Typography sx={carouselType.statLabel}>{cell.l}</Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>
            <Stack
              direction="row"
              spacing={2.5}
              sx={{
                bgcolor: "#fff",
                p: 3,
                borderRadius: 4,
                boxShadow: "0 20px 40px rgba(0,110,47,0.08)",
                border: `1px solid ${OUTLINE_VARIANT}`,
                alignItems: "flex-start",
              }}
            >
              <Box
                component="img"
                src={testimonialImage}
                alt="Partner chef"
                sx={{ width: 64, height: 64, borderRadius: "50%", objectFit: "cover", flexShrink: 0 }}
              />
              <Box>
                <Stack direction="row" spacing={0.25} sx={{ mb: 1 }}>
                  {[0, 1, 2, 3, 4].map((i) => (
                    <StarIcon key={i} sx={{ fontSize: 14, color: PRIMARY }} />
                  ))}
                </Stack>
                <Typography sx={{ ...carouselType.testimonialQuote, mb: 1 }}>
                  &ldquo;Tazty increased our delivery volume while reducing kitchen stress. The
                  interface is remarkably intuitive.&rdquo;
                </Typography>
                <Typography sx={carouselType.testimonialByline}>Tazty partner, Kitchen network</Typography>
              </Box>
            </Stack>
          </Box>
        </Slide>

        <Slide>
          <Box
            sx={{
              position: "absolute",
              top: -96,
              left: -96,
              width: 384,
              height: 384,
              borderRadius: "50%",
              bgcolor: `${PRIMARY_CONTAINER}33`,
              filter: "blur(48px)",
              pointerEvents: "none",
            }}
          />
          <Box sx={{ position: "relative", zIndex: 1, maxWidth: 520, mx: "auto" }}>
            <Box
              sx={{
                width: 96,
                height: 96,
                borderRadius: "20px",
                bgcolor: PRIMARY,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                mb: 4,
                boxShadow: `0 16px 40px ${PRIMARY}55`,
              }}
            >
              <BoltIcon sx={{ fontSize: 56, color: "#fff" }} />
            </Box>
            <Typography sx={{ ...carouselType.headlineLoose, mb: 2 }}>
              Easy <br />
              <Box component="span" sx={{ color: PRIMARY, fontStyle: "italic" }}>
                Onboarding
              </Box>
              .
            </Typography>
            <Typography sx={{ ...carouselType.body, maxWidth: 480, mb: 2 }}>
              Quick order accept and faster settlements to keep your kitchen running smoothly.
            </Typography>
            <Grid container spacing={2} sx={{ pt: 2 }}>
              {[
                { Icon: VerifiedIcon, t: "Instant Activation" },
                { Icon: PaymentsIcon, t: "Faster Settlements" },
                { Icon: TaskAltIcon, t: "Quick Order Accept" },
                { Icon: SupportAgentIcon, t: "Priority Desk" },
              ].map(({ Icon, t }) => (
                <Grid item xs={6} key={t}>
                  <Stack
                    direction="row"
                    spacing={1.5}
                    alignItems="center"
                    sx={{
                      ...carouselType.featureListItem,
                      bgcolor: "rgba(255,255,255,0.65)",
                      p: 2,
                      borderRadius: 2,
                      border: `1px solid ${OUTLINE_VARIANT}`,
                    }}
                  >
                    <Icon sx={{ color: PRIMARY, fontSize: 22 }} />
                    {t}
                  </Stack>
                </Grid>
              ))}
            </Grid>
          </Box>
        </Slide>

        <Slide sx={{ bgcolor: "#fafaf9" }}>
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              opacity: 0.04,
              pointerEvents: "none",
              backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M54.627 0l.83.83L.83 55.457l-.83-.83L54.627 0zM8.573 0l.83.83L0 9.403l-.83-.83L8.573 0z' fill='%23006e2f' fill-opacity='1' fill-rule='evenodd'/%3E%3C/svg%3E")`,
            }}
          />
          <Box sx={{ position: "relative", zIndex: 1, maxWidth: 576, mx: "auto", width: "100%" }}>
            <Stack spacing={4}>
              <Box>
                <Typography
                  component="span"
                  sx={{
                    display: "inline-block",
                    px: 2,
                    py: 0.75,
                    mb: 3,
                    ...carouselType.eyebrow,
                    bgcolor: `${PRIMARY_CONTAINER}33`,
                    borderRadius: 999,
                  }}
                >
                  Maximize Revenue
                </Typography>
                <Typography sx={{ ...carouselType.headline, mb: 3 }}>
                  Zero <br />
                  <Box component="span" sx={{ color: PRIMARY, fontStyle: "italic" }}>
                    Onboarding
                  </Box>{" "}
                  Fee.
                </Typography>
                <Typography sx={carouselType.body}>
                  Benefit from 30% less commissions and n+3 days quicker settlements within a week, maximizing your
                  restaurant&apos;s bottom line.
                </Typography>
              </Box>

              <Grid container spacing={3}>
                {[
                  { k: "₹0", l: "Onboarding Fee" },
                  { k: "30%", l: "Less Commission" },
                  { k: "n+3 days", l: "Faster Settlements" },
                ].map((cell) => (
                  <Grid item xs={4} key={cell.l}>
                    <Box
                      sx={{
                        p: 2,
                        bgcolor: "#fff",
                        borderRadius: 3,
                        border: `1px solid ${OUTLINE_VARIANT}`,
                        textAlign: "center",
                        boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
                      }}
                    >
                      <Typography sx={carouselType.statValue}>{cell.k}</Typography>
                      <Typography sx={carouselType.statLabel}>{cell.l}</Typography>
                    </Box>
                  </Grid>
                ))}
              </Grid>

              <Box
                sx={{
                  display: "inline-flex",
                  flexDirection: "column",
                  gap: 0.5,
                  p: 4,
                  bgcolor: PRIMARY,
                  color: "#fff",
                  borderRadius: "24px",
                  boxShadow: `0 20px 40px ${PRIMARY}44`,
                  alignSelf: "flex-start",
                }}
              >
                <Typography
                  sx={{
                    ...carouselType.eyebrow,
                    color: "#fff",
                    opacity: 0.9,
                  }}
                >
                  Partner Advantage
                </Typography>
                <Typography sx={{ fontSize: { xs: "1.375rem", sm: "1.625rem" }, fontWeight: 900, lineHeight: 1.15 }}>
                  Grow with Tazty
                </Typography>
                <Typography sx={{ fontSize: 10, fontWeight: 500, opacity: 0.85, mt: 1, fontStyle: "italic" }}>
                  *Illustrative — terms apply per agreement
                </Typography>
              </Box>
            </Stack>
          </Box>
        </Slide>

        <Slide
          sx={{
            background: "linear-gradient(to bottom, rgba(255,255,255,0.92), rgba(240,243,255,0.92))",
          }}
        >
          <Box sx={{ position: "relative", zIndex: 1, maxWidth: 560, mx: "auto", width: "100%" }}>
            <Stack spacing={3}>
              <Stack
                direction="row"
                alignItems="center"
                spacing={1}
                sx={{
                  display: "inline-flex",
                  px: 2,
                  py: 0.75,
                  borderRadius: 999,
                  bgcolor: `${PRIMARY_CONTAINER}1a`,
                  border: `1px solid ${PRIMARY_CONTAINER}33`,
                  width: "fit-content",
                }}
              >
                <TrendingUpIcon sx={{ fontSize: 18, color: PRIMARY }} />
                <Typography sx={carouselType.eyebrow}>Growth &amp; Visibility</Typography>
              </Stack>

              <Box>
                <Typography sx={{ ...carouselType.headline, mb: 2 }}>
                  Maximize Your Outreach.
                  <br />
                  <Box
                    component="span"
                    sx={{
                      display: "inline-block",
                      background: `linear-gradient(90deg, ${PRIMARY} 0%, ${PRIMARY_CONTAINER} 100%)`,
                      WebkitBackgroundClip: "text",
                      WebkitTextFillColor: "transparent",
                      backgroundClip: "text",
                      color: "transparent",
                    }}
                  >
                    Enable Hyperlocal
                  </Box>
                  <br />
                  Business Growth.
                </Typography>
                <Typography sx={{ ...carouselType.body, opacity: 0.88, maxWidth: 520 }}>
                  Connect with thousands of local customers and grow your brand in your neighborhood with
                  Tazty&apos;s precision-targeted logistics and marketing.
                </Typography>
              </Box>

              <Grid container spacing={3} sx={{ pt: 1 }}>
                <Grid item xs={12} sm={6}>
                  <Box
                    sx={{
                      position: "relative",
                      overflow: "hidden",
                      bgcolor: SURFACE_LOW,
                      p: 3,
                      borderRadius: 3,
                      border: `1px solid ${OUTLINE_VARIANT}`,
                      "&:hover .growth-stat-deco": { transform: "scale(1.08)" },
                    }}
                  >
                    <Box
                      className="growth-stat-deco"
                      sx={{
                        position: "absolute",
                        right: -16,
                        bottom: -16,
                        opacity: 0.1,
                        transition: "transform 0.5s ease",
                        pointerEvents: "none",
                      }}
                    >
                      <StraightenIcon sx={{ fontSize: 96, color: PRIMARY }} />
                    </Box>
                    <Box sx={{ position: "relative", zIndex: 1 }}>
                      <Typography sx={{ ...carouselType.statValue, fontWeight: 800, mb: 0.5 }}>3.2km</Typography>
                      <Typography sx={carouselType.statLabel}>Avg. Delivery Radius</Typography>
                    </Box>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Box
                    sx={{
                      position: "relative",
                      overflow: "hidden",
                      bgcolor: SURFACE_LOW,
                      p: 3,
                      borderRadius: 3,
                      border: `1px solid ${OUTLINE_VARIANT}`,
                      "&:hover .growth-stat-deco": { transform: "scale(1.08)" },
                    }}
                  >
                    <Box
                      className="growth-stat-deco"
                      sx={{
                        position: "absolute",
                        right: -16,
                        bottom: -16,
                        opacity: 0.1,
                        transition: "transform 0.5s ease",
                        pointerEvents: "none",
                      }}
                    >
                      <GroupsOutlinedIcon sx={{ fontSize: 96, color: PRIMARY }} />
                    </Box>
                    <Box sx={{ position: "relative", zIndex: 1 }}>
                      <Typography sx={{ ...carouselType.statValue, fontWeight: 800, mb: 0.5 }}>200+</Typography>
                      <Typography sx={carouselType.statLabel}>Hyperlocal Leads</Typography>
                    </Box>
                  </Box>
                </Grid>
              </Grid>
            </Stack>
          </Box>
        </Slide>
        <Slide sx={{ bgcolor: "#ecfdf5" }}>
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              opacity: 0.03,
              pointerEvents: "none",
              backgroundImage: `radial-gradient(${PRIMARY} 1.5px, transparent 1.5px)`,
              backgroundSize: "32px 32px",
            }}
          />
          <Box
            sx={{
              position: "absolute",
              bottom: -96,
              right: -96,
              width: 384,
              height: 384,
              borderRadius: "50%",
              bgcolor: `${PRIMARY}1a`,
              filter: "blur(48px)",
              pointerEvents: "none",
            }}
          />
          <Box
            sx={{
              position: "absolute",
              top: -96,
              left: -96,
              width: 384,
              height: 384,
              borderRadius: "50%",
              bgcolor: `${PRIMARY_CONTAINER}1a`,
              filter: "blur(48px)",
              pointerEvents: "none",
            }}
          />
          <Box sx={{ position: "relative", zIndex: 1, maxWidth: 576, mx: "auto", width: "100%" }}>
            <Stack spacing={3}>
              <Box>
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={1}
                  sx={{
                    display: "inline-flex",
                    px: 2,
                    py: 0.75,
                    mb: 2.5,
                    borderRadius: 999,
                    bgcolor: `${PRIMARY_CONTAINER}33`,
                  }}
                >
                  <LocalShippingOutlinedIcon sx={{ fontSize: 14, color: PRIMARY }} />
                  <Typography sx={carouselType.eyebrow}>Logistics Simplified</Typography>
                </Stack>
                <Typography sx={{ ...carouselType.headline, mb: 2.5 }}>
                    Reach and promote your <br />
                  <Box component="span" sx={{ color: PRIMARY, fontStyle: "italic" }}>
                    Restaurant across Madurai.
                  </Box>
                </Typography>
                <Typography sx={carouselType.body}>
                  Our hyper-local logistics network ensures your dishes reach hungry customers in every corner
                  of Madurai, from the temple city heart to the growing suburbs.
                </Typography>
              </Box>

              <Grid container spacing={2.5}>
                <Grid item xs={12} sm={6}>
                  <Stack
                    direction="row"
                    spacing={2}
                    alignItems="center"
                    sx={{
                      p: 3,
                      bgcolor: "#fff",
                      borderRadius: 3,
                      border: `1px solid ${OUTLINE_VARIANT}`,
                      boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
                      height: "100%",
                    }}
                  >
                    <Box
                      sx={{
                        width: 48,
                        height: 48,
                        borderRadius: 2,
                        bgcolor: `${PRIMARY}1a`,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: PRIMARY,
                        flexShrink: 0,
                      }}
                    >
                      <MapOutlinedIcon />
                    </Box>
                    <Box>
                      <Typography sx={carouselType.featureCardTitle}>Live Tracking</Typography>
                      <Typography sx={{ ...carouselType.statLabel, mt: 0.5 }}>
                        Real-time route optimization
                      </Typography>
                    </Box>
                  </Stack>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Stack
                    direction="row"
                    spacing={2}
                    alignItems="center"
                    sx={{
                      p: 3,
                      bgcolor: "#fff",
                      borderRadius: 3,
                      border: `1px solid ${OUTLINE_VARIANT}`,
                      boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
                      height: "100%",
                    }}
                  >
                    <Box
                      sx={{
                        width: 48,
                        height: 48,
                        borderRadius: 2,
                        bgcolor: `${PRIMARY}1a`,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: PRIMARY,
                        flexShrink: 0,
                      }}
                    >
                      <TrendingUpIcon />
                    </Box>
                    <Box>
                      <Typography sx={carouselType.featureCardTitle}>3x Reach</Typography>
                      <Typography sx={{ ...carouselType.statLabel, mt: 0.5 }}>
                        Tap into new neighborhoods
                      </Typography>
                    </Box>
                  </Stack>
                </Grid>
              </Grid>

              <Box
                sx={{
                  position: "relative",
                  height: { xs: 168, sm: 176 },
                  width: "100%",
                  mt: 0.5,
                  borderRadius: 3,
                  overflow: "hidden",
                  border: `1px solid rgba(188,203,185,0.15)`,
                  boxShadow: "0 20px 40px rgba(0,0,0,0.12)",
                  "&:hover .delivery-hero-img": { transform: "scale(1.25)" },
                }}
              >
                <Box
                  component="img"
                  className="delivery-hero-img"
                  src={DELIVERY_HERO_IMAGE}
                  alt="Delivery in Madurai"
                  sx={{
                    position: "absolute",
                    left: 0,
                    width: "100%",
                    height: "118%",
                    top: "-10%",
                    objectFit: "cover",
                    objectPosition: "center 38%",
                    transition: "transform 700ms ease",
                    display: "block",
                  }}
                />
                <Box
                  sx={{
                    position: "absolute",
                    inset: 0,
                    background: "linear-gradient(to top, rgba(0,0,0,0.4), transparent)",
                    pointerEvents: "none",
                  }}
                />
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={0.75}
                  sx={{
                    position: "absolute",
                    bottom: 15,
                    left: 24,
                    ...carouselType.imageCaption,
                  }}
                >
                  <LocationOnIcon sx={{ fontSize: 18, color: "#fff" }} />
                  <Typography component="span" sx={carouselType.imageCaption}>
                    Temple Road, Madurai
                  </Typography>
                </Stack>
              </Box>
            </Stack>
          </Box>
        </Slide>

      </CarouselTrack>

      <Stack
        direction="row"
        spacing={2}
        justifyContent="center"
        sx={{ position: "absolute", bottom: 48, left: 0, right: 0, zIndex: 2 }}
      >
        <Dot $delay="0s" />
        <Dot $delay="-24s" />
        <Dot $delay="-18s" />
        <Dot $delay="-12s" />
        <Dot $delay="-6s" />
      </Stack>
    </CarouselRoot>
  );
};

export default LoginMarketingCarousel;
